import {
	Component,
	computed,
	ElementRef,
	HostListener,
	inject,
	signal,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core'
import { TranslatePipe } from '@ngx-translate/core'
import { Router, RouterLink } from '@angular/router'
import Keycloak from 'keycloak-js'
import { Button } from '@openng/optimus-ui/button'
import { MenuItem } from '@openng/optimus-ui/api'
import { TooltipModule } from '@openng/optimus-ui/tooltip'
import { LanguageService } from '../../core/language.service'
import { LayoutService } from '../ui/layout.service'
import { User } from '../state/user'
import { AppUserGetAccountRecLegalAccountStatusEnum } from 'api'
import { StyleClass } from '@openng/optimus-ui/styleclass'
import { CacheService } from '../cache-service/cache.service'
import { Keys } from '../cache-service/keys'
import { CacheType } from '../cache-service/cache-type.enum'
import { HydrationStateService } from '../../core/hydration-state.service'

// import { WebSocketService } from '../../services/web-socket-service'

@Component({
	standalone: true,
	selector: 'app-header',
	imports: [TranslatePipe, RouterLink, Button, TooltipModule, StyleClass],
	templateUrl: './header.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './header.scss',
})
export class HeaderComponent {
	readonly langService = inject(LanguageService)
	protected readonly keycloakService = inject(Keycloak)
	// readonly webSocketService = inject(WebSocketStomp)
	readonly #router = inject(Router)
	readonly layoutService = inject(LayoutService)
	#user = inject(User)
	readonly #cacheService = inject(CacheService)
	/**
	 * گیت رندر همسان سرور/کلاینت: سرور Keycloak را init نمی‌کند و همیشه «مهمان» است؛
	 * تا پایان hydration کلاینت هم همان حالت را رندر می‌کنیم تا NG0500 رخ ندهد و
	 * دکمه‌های هدر listener خود را از دست ندهند.
	 */
	readonly hydration = inject(HydrationStateService)
	// readonly SocketClientState = SocketClientState

	// ✅ سیگنالی برای نگهداری زمان فعلی
	currentTime = signal(new Date())
	clockInterval: any
	today = new Date().toISOString()

	// ✅ یک سیگنال جدید برای وضعیت اتصال
	// connectionState = toSignal(this.webSocketService.getState(), { initialValue: SocketClientState.DISCONNECTED })
	// connectionStateCssClass = computed(() => {
	// 	switch (this.connectionState()) {
	// 		case SocketClientState.CONNECTED:
	// 			return 'status-connected'
	// 		case SocketClientState.ATTEMPTING:
	// 			return 'status-attempting'
	// 		case SocketClientState.DISCONNECTED:
	// 			return 'status-disconnected'
	// 		default:
	// 			return 'status-disconnected'
	// 	}
	// })

	menuOpen = signal(false)

	items = signal<MenuItem[]>([
		{ label: '🇬🇧 English', target: 'en', icon: '🇬🇧', command: () => this.setLang('en') },
		{ label: 'العربیه 🇸🇦', target: 'ar', icon: '🇸🇦', command: () => this.setLang('ar') },
		{ label: 'پارسی 🇮🇷', target: 'fa', icon: '🇮🇷', command: () => this.setLang('fa') },
	])
	selectedLangItem = computed(() => this.items().find(f => f.target === this.langService.currentLanguage()))
	isShahkarVerified = computed(() => this.#user.getUser()().shahkarVerified ?? false)
	legalAccountStatus = computed<AppUserGetAccountRecLegalAccountStatusEnum | null>(
		() => this.#user.getUser()().legalAccountStatus ?? null
	)
	profileImageFileId = computed(() => this.#user.getUser()().profileImageFileId)
	firstName = computed(() => this.#user.getUser()().firstName ?? '')

	toggleMenu(event: MouseEvent) {
		event.stopPropagation()
		this.menuOpen.update(prev => !prev)
	}

	@HostListener('document:click')
	closeMenu() {
		this.menuOpen.set(false)
	}

	@ViewChild('menu') menu!: ElementRef
	@ViewChild('menuBtn') menuBtn!: ElementRef

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event) {
		const target = event.target as HTMLElement

		if (
			this.menu &&
			this.menuBtn &&
			!this.menu.nativeElement.contains(target) &&
			!this.menuBtn.nativeElement.contains(target)
		) {
			this.menuOpen.set(false)
		}
	}

	setLang(lang: 'fa' | 'en' | 'ar') {
		this.langService.setLanguage(lang)
	}

	public login(): void {
		// void this.#router.navigate(['/login'])
		if (this.keycloakService.authenticated) {
			void this.#router.navigate(['/dashboard'])
		} else {
			this.#cacheService.setItem(Keys.FirstLogin, true, CacheType.SESSION_STORAGE)
			void this.keycloakService.login({ redirectUri: window.location.origin + '/login' })
		}
	}

	logout() {
		// this.webSocketService.disconnect()
		void this.keycloakService.logout({ redirectUri: window.location.origin + '/' })
	}

	goToLanding() {
		this.layoutService.showLanding.set(true)
		this.#router.navigate(['/'])
	}

	goToPanel() {
		this.layoutService.showLanding.set(false)
		this.#router.navigate(['/dashboard'])
	}

	toggleDarkMode() {
		const element = document.querySelector('html')
		if (element) {
			element.classList.toggle('my-app-dark')
		}
		this.layoutService.layoutConfig.update(state => ({ ...state, darkTheme: !state.darkTheme }))
	}

	moveToTadaPage(page: 'contact-us' | 'about-us') {
		switch (page) {
			case 'contact-us':
				window.location.replace('https://www.tada.market/contact-us/')
				break
			case 'about-us':
				window.location.replace('https://www.tada.market/about-us/')
				break
		}
	}

	protected readonly AppUserGetAccountRecLegalAccountStatusEnum = AppUserGetAccountRecLegalAccountStatusEnum
}
